import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { DefinitionTooltip } from "src/components/common";
import type { Campaign, CampaignDashboardText } from "./CampaignDashboardTypes";

interface MetricCell {
  key: string;
  label: string;
  kind: "count" | "percent" | "unavailable";
  field?: string;
  formattedField?: string;
  unavailableNote?: string;
}

const buildMetricCells = (t: CampaignDashboardText): Array<MetricCell | null> => [
  { key: "conversions", label: t.conversions, kind: "count", field: "conversions", formattedField: "conversionsFormatted" },
  { key: "audience", label: t.totalAudience, kind: "count", field: "audience_size", formattedField: "audience_sizeFormatted" },
  { key: "totalUsers", label: t.totalUsersOnWebsite, kind: "count", field: "total_users", formattedField: "total_usersFormatted" },
  { key: "newUsers", label: t.newUsers, kind: "unavailable", unavailableNote: t.metricUnavailable },
  { key: "openRate", label: t.openRate, kind: "percent", field: "open_rate", formattedField: "open_rateFormatted" },
  { key: "conversionRate", label: t.conversionRate, kind: "percent", field: "conversion_rate", formattedField: "conversion_rateFormatted" },
  { key: "emailsSent", label: t.totalEmailsSent, kind: "count", field: "delivered", formattedField: "deliveredFormatted" },
  { key: "opens", label: t.opens, kind: "count", field: "opens", formattedField: "opensFormatted" },
  { key: "repeatedUsers", label: t.repeatedUsers, kind: "unavailable", unavailableNote: t.metricUnavailable },
  { key: "bounceRate", label: t.bounceRate, kind: "percent", field: "bounce_rate", formattedField: "bounce_rateFormatted" },
  { key: "clickRate", label: t.clickRate, kind: "percent", field: "click_rate", formattedField: "click_rateFormatted" },
  null,
];

interface CampaignMetricsPanelProps {
  record: Campaign;
  text: CampaignDashboardText;
  formatInteger: (_value: number) => string;
  formatPercentValue: (_value: number) => string;
}

const CampaignMetricsPanel: FC<CampaignMetricsPanelProps> = ({
  record,
  text,
  formatInteger,
  formatPercentValue,
}) => {
  const { t: translate } = useTranslation();

  return (
    <div className="campaign-metrics-panel">
      {buildMetricCells(text).map((cell, index) => {
        if (!cell) {
          return (
            <div
              key={`empty-${index}`}
              className="campaign-metrics-panel__cell campaign-metrics-panel__cell--empty"
              aria-hidden
            />
          );
        }
        const rawValue = Number(record[cell.field as string] ?? 0);
        const formatted =
          cell.formattedField && typeof record[cell.formattedField] === "string"
            ? String(record[cell.formattedField]).trim()
            : "";
        const value =
          cell.kind === "unavailable"
            ? "—"
            : formatted ||
              (cell.kind === "percent" ? formatPercentValue(rawValue) : formatInteger(rawValue));
        return (
          <div key={cell.key} className="campaign-metrics-panel__cell">
            <span className="campaign-metrics-panel__label">
              {cell.label}
              <DefinitionTooltip
                label={cell.label}
                info={{
                  description:
                    cell.kind === "unavailable"
                      ? cell.unavailableNote ?? text.metricUnavailableFallback
                      : text.metricForThisCampaign.replace("{{label}}", cell.label),
                }}
                formulaTitle={translate("engagementOverview.formula")}
              />
            </span>
            <span className="campaign-metrics-panel__value">{value}</span>
          </div>
        );
      })}
    </div>
  );
};

export default CampaignMetricsPanel;
